import { STATE_VALUES } from "../constants";
import { ISelectOption } from "../../../components/common/SearchableSelect/SearchableSelect";
import IAccountData from "../../../models/IAccountData";
import { IPatientDetail } from "@curefit/care-common";
import { IUserFitnessPlanRequestParams } from "../../Patient/Transform/interface";

export interface IMapStateToProps {
    userProfile: IPatientDetail;
    accountData: IAccountData;
    allExercises: ISelectOption[];
    userFitnessPlan: any;
    userFitnessPlanStartDate: string | number;
    isUserPlanPublished: boolean;
}

export interface IMapDispatchToProps {
    fetchFitnessTemplateById: (microcycleId: number) => any;
    fetchAllExercises: () => any;
    saveFitnessTemplate: (requestBody: IMicrocycle) => any;
    assignFitnessPlan?: (reqParams: IUserFitnessPlanRequestParams, selectedPlanId: number) => Promise<IMicrocycle>;
    savePublishedUserFitnessPlan: (requestBody: IUserFitnessPlan) => Promise<IUserFitnessPlan>;
    setUserFitnessPlan?: (userPlan: any, startDate: string | number, userProfile: any, isPlanPublished: boolean) => void;
    saveUserFitnessPlan?: (fitnessPlan: any) => void;
    deleteUserFitnessPlan?: (planId: number) => Promise<any>;
}

export enum formFieldNames {
    GOALID = "goalId",
    LEVEL = "level",
    WORKOUTDAYS = "workoutDays",
    TEMPLATENAME = "description",
    GENDER = "gender",
    REGIMENID = "regimenId",
    ROLLOUTPERCENTAGE = "rolloutPercentage",
    USERPLANSTARTDATE = "userPlanStartDate",
    USERID = "userId",
    //prewods
    WORKOUTTYPE = "workoutType",
    DAYOFCYCLE = "dayOfCycle",
    BLOCKTYPE = "blockType",
    SETTYPE = "setType",
    REPITITION = "repetition",
    //managed exercise
    EXERCISEID = "exerciseId",
    EXERCISETYPE = "exerciseType",
    COMMENTS = "trainerComments",
    DURATION_IN_SECS = "expectedDurationInSeconds",
    //executions
    TYPE = "type",
    WEIGHT = "weight",
    WEIGHTUNIT = "weightUnit",
    MINBODYWEIGHTPERCENTAGE = "minBodyWeightPercentage",
    MAXBODYWEIGHTPERCENTAGE = "maxBodyWeightPercentage",
    DURATION = "duration",
    MINDURATION = "minDuration",
    MAXDURATION = "maxDuration",
    COUNT = "count",
}

export interface IMicrocycle extends ICommonProperties {
    prewods: IPrewod[];
    [formFieldNames.REGIMENID]: number;
    [formFieldNames.GOALID]: number;
    [formFieldNames.LEVEL]: string;
    [formFieldNames.TEMPLATENAME]: string;
    [formFieldNames.WORKOUTDAYS]: number;
    [formFieldNames.ROLLOUTPERCENTAGE]?: number;
    [formFieldNames.USERPLANSTARTDATE]?: string;
    [formFieldNames.USERID]?: string;
    meta: IMicrocycleMeta;
    state?: STATE_VALUES;
    status?: string;
}

interface IMicrocycleMeta {
    [formFieldNames.GENDER]: string;
}

export interface IPrewod extends ICommonProperties {
    regimenId?: number;
    goalId?: number;
    level?: string;
    workoutDays?: number;
    microcycleId?: number;
    [formFieldNames.WORKOUTTYPE]: string;
    [formFieldNames.DAYOFCYCLE]: number;
    sets: ISet[];
    workoutName?: string;
}

export interface ISet extends ICommonProperties {
    [formFieldNames.BLOCKTYPE]: string;
    [formFieldNames.SETTYPE]: string;
    [formFieldNames.REPITITION]: number;
    [formFieldNames.COMMENTS]: string;
    [formFieldNames.DURATION_IN_SECS]: number;
    managedExercises: IManagedExercise[];
    exercises?: IManagedExercise[];
    sequence?: number;
    managedPreWodId?: number;
    status?: string;
}

export interface IManagedExercise extends ICommonProperties {
    [formFieldNames.EXERCISEID]: string;
    [formFieldNames.EXERCISETYPE]: string;
    sequence?: number;
    executions: IExecution[];
    [formFieldNames.COMMENTS]: string;
    managedWorkoutSetId?: number;
    status?: string;
    userId?: string;
}

export interface IExecution {
    [formFieldNames.TYPE]: "TIMED" | "COUNT" | "TIMED_COUNT";
    [formFieldNames.WEIGHT]: number;
    [formFieldNames.WEIGHTUNIT]: string;
    [formFieldNames.MINBODYWEIGHTPERCENTAGE]?: number;
    [formFieldNames.MAXBODYWEIGHTPERCENTAGE]?: number;
    [formFieldNames.DURATION]?: number;
    [formFieldNames.MINDURATION]?: number;
    [formFieldNames.MAXDURATION]?: number;
    [formFieldNames.COUNT]?: number;
}

export interface IGoal {
    id: number;
    createdOn: Date;
    lastModifiedOn: Date;
    version: number;
    name: string;
    description: any;
    herculesGoalId: string;
}

export interface IPaginatedGoals {
    offset: number;
    limit: number;
    totalElements: number;
    elements: IGoal[];
}

export interface IPaginatedMicrocycles {
    offset: number;
    limit: number;
    totalElements: number;
    elements: IMicrocycle[];
}

export interface ICommonProperties {
    id?: number;
    createdOn?: Date;
    lastModifiedOn?: Date;
    tenantId?: number;
    createdBy?: string | number;
}

interface IFitnessTemplate {
    params: {
        fitnessTemplateId: number;
        userId: string;
    }
}

export interface IMicrocycleError {
    message?: string;
    identifier?: string;
    fieldName?: string;
    day?: number;
    phase?: string;
    exerciseIndex?: number;
    workoutIndex?: number;
}

export interface IUserFitnessPlan {
    microCycleEntry: IUserTemplateMicrocycle;
    userGoalEntry: IUserGoalEntry;
}

interface IUserTemplateMicrocycle {
    createdBy: string | number;
    createdOn: Date;
    cycleLength: number;
    [formFieldNames.TEMPLATENAME]: string;
    id: number;
    lastModifiedOn: Date;
    managedMicrocycleId: number;
    sequence: number;
    startDate: Date;
    status: string;
    tenantId: number;
    [formFieldNames.USERID]: string;
    userWods: IPrewod[];
}

interface IUserGoalEntry extends ICommonProperties {
    goal: {
        id: number;
    }
    regimen: {
        id: number;
    }
    tenant: {
        id: number
    }

    workoutsPerWeek: number;
}


export interface IFitnessTemplateProps extends IFitnessTemplate, IMapDispatchToProps, IMapStateToProps { }